  /**
   * Product variant selection and availability system
   * Handles option selection, variant compatibility, and media updates
   */
  (function() {
    // Constants
    const SELECTORS = {
      OPTION_INPUTS: '.option-btn, .color-swatch',
      PRODUCT_CARD: '.product-card',
      PRODUCT_DATA: 'script[data-product-data]',
      OPTION_BUTTONS: '.option-buttons',
      OPTION_ELEMENT: '[data-option]',
      ACTIVE_OPTIONS: '.option-btn.active, .color-swatch.active',
      PRODUCT_MEDIA: '.product-card__media',
      BOTTOM_BUTTON: 'a.product-card__bottom-btn',
      PRODUCT_LINK: 'a.variant.media',
      PRICE_ELEMENT: '.price',
      PRICE_CURRENT: '.price__standard',
      PRICE_COMPARE: '.price__compare',
      BADGE: '.product-card__media .badges',
    };

    const CSS_CLASSES = {
      ACTIVE: 'active',
      DISABLED: 'disabled'
    };

    const ATTRIBUTES = {
      DATA_VALUE: 'data-value',
      DATA_OPTION: 'data-option',
      DATA_PRODUCT_ID: 'data-product-id'
    };

    // Utility Functions
    /**
     * Builds an index of available variant options
     * @param {Array} variants - Array of product variants
     * @returns {Object} Index containing available variants
     */
    function buildAvailableVariantIndex(variants) {
      const availableVariants = variants
        // .filter(variant => variant.available)
        .map(variant => variant.options);
      
      return { availableVariants };
    }

    /**
     * Gets compatible option values based on current selections
     * @param {Object} index - Variant index from buildAvailableVariantIndex
     * @param {Array} selectedValues - Sparse array with selected values at their correct positions
     * @param {Array} productOptions - Product options array to determine total option count
     * @returns {Array} Array of compatible option values
     */
    function getCompatibleValues(index, selectedValues, productOptions) {
      const totalOptions = productOptions.length;
      const compatibleValues = Array.from({ length: totalOptions }, () => new Set());

      for (let i = 0; i < totalOptions; i++) {
        for (const variant of index.availableVariants) {
          let matches = true;

          // Check if this variant matches all other selected options
          for (let j = 0; j < totalOptions; j++) {
            if (j === i) continue;
            if (selectedValues[j] && variant[j] !== selectedValues[j]) {
              matches = false;
              break;
            }
          }

          if (matches) {
            compatibleValues[i].add(variant[i]);
          }
        }
      }

      return compatibleValues.map(set => Array.from(set)).flat();
    }

    /**
     * Finds the position of an option value within product options
     * @param {Array} options - Product options array
     * @param {string} value - Option value to find
     * @returns {number|null} Position of the option or null if not found
     */
    function getOptionPosition(options, value) {
      for (const option of options) {
        if (option.values.includes(value)) {
          return option.position;
        }
      }
      return null;
    }

    // Core Functions
    /**
     * Safely parses product data from the DOM
     * @param {Element} productCard - Product card element
     * @returns {Object|null} Parsed product data or null if invalid
     */
    function getProductData(productCard) {
      try {
        const productJSON = productCard.querySelector(SELECTORS.PRODUCT_DATA);
        if (!productJSON || !productJSON.textContent) {
          throw new Error('Product data script not found');
        }
        return JSON.parse(productJSON.textContent);
      } catch (error) {
        console.error('Error parsing product data:', error);
        return null;
      }
    }

    /**
     * Updates active states for option buttons
     * @param {Element} clickedInput - The clicked option input
     * @param {Element} optionButtons - Container for option buttons
     */
    function updateActiveStates(clickedInput, optionButtons) {
      const optionElements = optionButtons.querySelectorAll(SELECTORS.OPTION_ELEMENT);
      
      optionElements.forEach(element => {
        if (element !== clickedInput) {
          element.classList.remove(CSS_CLASSES.ACTIVE);
        }
      });
      
      clickedInput.classList.add(CSS_CLASSES.ACTIVE);
    }

    /**
     * Updates option availability based on variant compatibility
     * @param {Element} productCard - Product card element
     * @param {Object} productData - Product data object
     * @param {string} changedOption - The changed option value
     */
    function updateOptionAvailability(productCard, productData, changedOption) {
      const index = buildAvailableVariantIndex(productData.variants);
      const activeOptions = productCard.querySelectorAll(SELECTORS.ACTIVE_OPTIONS);
      
      // Build selectedValues as a sparse array using actual option positions
      const selectedValues = new Array(productData.options.length);
      activeOptions.forEach(option => {
        const value = option.getAttribute(ATTRIBUTES.DATA_VALUE);
        const position = getOptionPosition(productData.options, value);
        if (position !== null) {
          selectedValues[position - 1] = value; // Convert to 0-based index
        }
      });

      const compatibleValues = getCompatibleValues(index, selectedValues, productData.options);
      
      const allOptions = productCard.querySelectorAll(SELECTORS.OPTION_INPUTS);
      allOptions.forEach(optionInput => {
        const optionValue = optionInput.getAttribute(ATTRIBUTES.DATA_VALUE);
        
        if (compatibleValues.includes(optionValue)) {
          optionInput.classList.remove(CSS_CLASSES.DISABLED);
        } else {
          optionInput.classList.add(CSS_CLASSES.DISABLED);
        }
      });
    }

    /**
     * Finds the matching variant based on selected options
     * @param {Object} productData - Product data object
     * @param {Array} selectedValues - Sparse array with selected values at their correct positions
     * @param {Array} renderedPositions - Array of option positions that have frontend selectors
     * @returns {Object|null} Matching variant or null if not found
     */
    function findMatchingVariant(productData, selectedValues, renderedPositions) {
      for (const variant of productData.variants) {
        let matches = true;
        
        // Check if this variant matches all selected options (only for rendered positions)
        for (const position of renderedPositions) {
          const positionIndex = position - 1; // Convert to 0-based
          if (selectedValues[positionIndex] && variant.options[positionIndex] !== selectedValues[positionIndex]) {
            matches = false;
            break;
          }
        }
        
        if (matches) {
          return variant;
        }
      }
      
      return null;
    }

    /**
     * Updates product media based on selected options
     * @param {Element} productCard - Product card element
     */
    function updateProductMedia(productCard) {
      const productId = productCard.getAttribute(ATTRIBUTES.DATA_PRODUCT_ID);
      const activeOptions = productCard.querySelectorAll(SELECTORS.ACTIVE_OPTIONS);
      const allOptions = productCard.querySelectorAll(SELECTORS.OPTION_INPUTS);
      
      const productData = getProductData(productCard);
      if (!productData) return;
      
      // Get product URL
      const productLink = productCard.querySelector(SELECTORS.PRODUCT_LINK);
      if (!productLink) return;
      
      // Determine which option positions are actually rendered on the frontend
      const renderedPositions = new Set();
      allOptions.forEach(option => {
        const value = option.getAttribute(ATTRIBUTES.DATA_VALUE);
        const position = getOptionPosition(productData.options, value);
        if (position !== null) {
          renderedPositions.add(position);
        }
      });

      // Build selectedValues as a sparse array using actual option positions
      const selectedValues = new Array(productData.options.length);
      activeOptions.forEach(option => {
        const value = option.getAttribute(ATTRIBUTES.DATA_VALUE);
        const position = getOptionPosition(productData.options, value);
        if (position !== null && renderedPositions.has(position)) {
          selectedValues[position - 1] = value; // Convert to 0-based index
        }
      });

      // Find the matching variant
      const matchingVariant = findMatchingVariant(productData, selectedValues, Array.from(renderedPositions));
      if (!matchingVariant) return;
      
      const productUrl = productLink.href.split('?')[0];
      const newProductUrl = `${productUrl}?variant=${matchingVariant.id}`;
      productLink.href = newProductUrl;
      const bottomButton = productCard.querySelector(SELECTORS.BOTTOM_BUTTON);
      if (bottomButton) {
        bottomButton.href = newProductUrl;
      }
      const fetchUrl = `${newProductUrl}&view=card`;
      
      // Fetch updated product card
      fetch(fetchUrl)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then(html => {
          const parser = new DOMParser();
          const sourceDoc = parser.parseFromString(html, 'text/html');
          const sourceMedia = sourceDoc.querySelector(
            `[${ATTRIBUTES.DATA_PRODUCT_ID}="${productId}"] ${SELECTORS.PRODUCT_MEDIA}`
          );
          const destinationMedia = productCard.querySelector(SELECTORS.PRODUCT_MEDIA);

          const sourceImage = sourceMedia.querySelector('img');
          const sourceImageUrl = sourceImage.src;
          
          if (sourceMedia && destinationMedia) {
            const sourceImage = sourceMedia.querySelector('img');
            const destinationImage = destinationMedia.querySelector('img');

            const secondarySourceImage = sourceMedia.querySelector('template').content.querySelector('.media--secondary img');
            const secondaryDestinationImage = destinationMedia.querySelector('.media--secondary img');
            
            // Array of attributes to copy
            const imageAttributes = ['src', 'srcset', 'alt'];
            
            // Copy all attributes in one loop
            imageAttributes.forEach(attr => {
              const sourceAttribute = sourceImage.getAttribute(attr);
              const destinationAttribute = destinationImage.getAttribute(attr);

              if (sourceAttribute && destinationAttribute) {
                destinationImage.setAttribute(attr, sourceAttribute);
              }
            });

            const secondarySourceAttribute = secondarySourceImage.getAttribute('data-srcset');
            if (secondarySourceAttribute) {
              secondaryDestinationImage.setAttribute('srcset', secondarySourceAttribute);
            }
          }

          const sourceBadge = sourceMedia.querySelector(SELECTORS.BADGE);
          const destinationBadge = destinationMedia.querySelector(SELECTORS.BADGE);
          if (sourceBadge && destinationBadge) {
            destinationBadge.innerHTML = sourceBadge.innerHTML;
          } else if (!sourceBadge && destinationBadge) {
            destinationBadge.innerHTML = "";
          }
        })
        .catch(error => {
          console.error('Error updating product media:', error);
        });
    }

     /**
     * Updates product price based on selected variant
     * @param {Element} productCard - Product card element
     */
     function updateProductPrice(productCard) {
      const activeOptions = productCard.querySelectorAll(SELECTORS.ACTIVE_OPTIONS);
      const allOptions = productCard.querySelectorAll(SELECTORS.OPTION_INPUTS);
      
      const productData = getProductData(productCard);
      if (!productData) return;
      
      // Determine which option positions are actually rendered on the frontend
      const renderedPositions = new Set();
      allOptions.forEach(option => {
        const value = option.getAttribute(ATTRIBUTES.DATA_VALUE);
        const position = getOptionPosition(productData.options, value);
        if (position !== null) {
          renderedPositions.add(position);
        }
      });

      // Build selectedValues as a sparse array using actual option positions
      const selectedValues = new Array(productData.options.length);
      activeOptions.forEach(option => {
        const value = option.getAttribute(ATTRIBUTES.DATA_VALUE);
        const position = getOptionPosition(productData.options, value);
        if (position !== null && renderedPositions.has(position)) {
          selectedValues[position - 1] = value; // Convert to 0-based index
        }
      });

      // Find the matching variant
      const matchingVariant = findMatchingVariant(productData, selectedValues, Array.from(renderedPositions));
      if (!matchingVariant) return;
      
      // Update price display
      const priceElement = productCard.querySelector(SELECTORS.PRICE_ELEMENT);
      if (!priceElement) return;
      
      const currentPriceElement = priceElement.querySelector(SELECTORS.PRICE_CURRENT);
      let comparePriceElement = priceElement.querySelector(SELECTORS.PRICE_COMPARE);
      if (!comparePriceElement) {
        const template = `
          <span class="price__compare inline-flex items-center h-auto relative"></span>
        `;
        comparePriceElement = priceElement.insertAdjacentHTML('beforeend', template);
      }

      if (currentPriceElement && matchingVariant.price) {
        currentPriceElement.textContent = matchingVariant.price;
      }
      
      if (comparePriceElement && matchingVariant.compare_at_price && matchingVariant.compare_at_price !== "$0" && matchingVariant.price !== matchingVariant.compare_at_price) {
        comparePriceElement.textContent = matchingVariant.compare_at_price;
        comparePriceElement.style.display = 'inline';
      } else if (comparePriceElement) {
        comparePriceElement.style.display = 'none';
      }

    }

    /**
     * Handles option input click events
     * @param {Event} event - Click event
     */
    function handleOptionClick(event) {
      event.preventDefault();
      
      const input = event.target;
      const productCard = input.closest(SELECTORS.PRODUCT_CARD);
      const optionButtons = input.closest(SELECTORS.OPTION_BUTTONS);
      
      if (!productCard || !optionButtons) return;
      
      const productData = getProductData(productCard);
      if (!productData) return;
      
      // Update active states
      updateActiveStates(input, optionButtons);
      
      // Get changed option details
      const changedOption = input.getAttribute(ATTRIBUTES.DATA_VALUE);
      if (!changedOption) return;
      
      // Update option availability
      updateOptionAvailability(productCard, productData, changedOption);
      
      // Update product media
      updateProductMedia(productCard);

      // Update product price
      updateProductPrice(productCard);
    }

    /**
     * Initializes option availability for a product card
     * @param {Element} productCard - Product card element
     */
    function initializeProductCardOptions(productCard) {
      const productData = getProductData(productCard);
      if (!productData) return;
      
      const activeOptions = productCard.querySelectorAll(SELECTORS.ACTIVE_OPTIONS);
      if (activeOptions.length === 0) return;
      
      // Get any active option to use for availability calculation
      const firstActiveOption = activeOptions[0];
      const changedOption = firstActiveOption.getAttribute(ATTRIBUTES.DATA_VALUE);
      
      if (changedOption) {
        updateOptionAvailability(productCard, productData, changedOption);
      }
    }

    /**
     * Initializes the variant selection system
     */
    function init() {
      // const section = document.querySelector(`#shopify-section-{{ section.id }}`);
      // const optionInputs = document.querySelectorAll(SELECTORS.OPTION_INPUTS);
      const productCards = document.querySelectorAll(SELECTORS.PRODUCT_CARD);
      console.log("productCards", productCards);

      productCards.forEach(card => {
        const optionInputs = card.querySelectorAll(SELECTORS.OPTION_INPUTS);
        console.log("option inputs", optionInputs);
        optionInputs.forEach(input => {
          input.addEventListener('click', handleOptionClick);
        });

        initializeProductCardOptions(card);
      });
      
      // Initialize option availability for each product card
      // const productCards = document.querySelectorAll(SELECTORS.PRODUCT_CARD);
      // productCards.forEach(productCard => {
      //   initializeProductCardOptions(productCard);
      // });
    }

    // Initialize when DOM is ready
    window.addEventListener('load', () => {
        setTimeout(() => {
            init();
        }, 1000);
    });

    // === Discount updater on load + variant change ===
function applyPromoDiscount(productCard) {
  const promoLine = productCard.querySelector('.product-card__promo-line');
  if (!promoLine) return;

  const basePriceEl = productCard.querySelector('.price__standard');
  const promoPriceEl = promoLine.querySelector('.product-card__price');
  if (!basePriceEl || !promoPriceEl) return;

  const rawPrice = basePriceEl.textContent.replace(/[^0-9.]/g, '');
  const price = parseFloat(rawPrice);
  if (isNaN(price)) return;

  const pctRaw = promoLine.getAttribute('data-discount-percentage') || '0';
  const pct = parseFloat(pctRaw.replace(/[^\d.-]/g, '')) || 0;

  // Apply discount
  if (pct > 0) {
    const discounted = (price * (1 - pct / 100)).toFixed(2);
    const formatted = discounted.endsWith('.00') ? Math.round(discounted).toString() : discounted;
    promoPriceEl.textContent = `$${formatted}`;
  } else {
    const formattedBase = price % 1 === 0 ? price.toFixed(0) : price.toFixed(2);
    promoPriceEl.textContent = `$${formattedBase}`;
  }
}


function initPromoDiscounts() {
  const productCards = document.querySelectorAll('.product-card');
  productCards.forEach(card => {
    applyPromoDiscount(card);

    // Watch for price changes in this card
    const priceEl = card.querySelector('.price__standard');
    if (priceEl) {
      let debounceTimer;
      const observer = new MutationObserver(() => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          applyPromoDiscount(card);
        }, 120); // delay ensures Shopify’s price update is complete
      });
      observer.observe(priceEl, { childList: true, subtree: true, characterData: true });
    }
  });
}

// Run once after all prices are rendered
window.addEventListener('load', () => {
  setTimeout(() => {
    initPromoDiscounts();
  }, 500);
});

  })();
